import { ButtonError, ButtonLight, ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { RowBetween } from 'components/Row'
import { DynamicSection } from 'pages/AddLiquidity/styled'
import React, { useCallback } from 'react'
import { TYPE } from 'theme'
import { Text } from 'rebass'
import { Field } from 'state/mint/actions'
import { ApprovalState } from 'hooks/useApproveCallback'
import { Dots } from 'pages/Pool/styleds'
import { TokenAmount } from '@uniswap/sdk-core'
import { ToggleOption } from 'pages/AddLiquidityV2/styled'
import { ToggleWrapper } from 'components/Toggle/MultiToggle'
import { PresetPriceRange } from 'pages/AddLiquidity'
import Modal from 'components/Modal'
import { ArrowWrapper } from 'components/swap/styleds'

interface Step3Props {
  activeStep: number
  tickLower?: number
  tickUpper?: number
  invalidPool: boolean
  invalidRange: boolean
  hasExistingPosition: boolean
  formattedAmounts: Record<string, string>
  maxAmounts: Record<string, any>
  atMaxAmounts: { CURRENCY_A?: TokenAmount | undefined; CURRENCY_B?: TokenAmount | undefined }
  currencies: Record<string, any>
  usdcValues: Record<string, any>
  onFieldAInput: (val: string) => void
  onFieldBInput: (val: string) => void
  depositADisabled: boolean
  depositBDisabled: boolean
  addIsUnsupported: boolean
  account?: string | null
  toggleWalletModal: () => void
  approvalA: ApprovalState
  approvalB: ApprovalState
  approveACallback: () => void
  approveBCallback: () => void
  showApprovalA: boolean
  showApprovalB: boolean
  isValid: boolean
  parsedAmounts: Record<string, any>
  expertMode: boolean
  onAdd: () => void
  setShowConfirm: (val: boolean) => void
  attemptingTxn: boolean
  errorMessage: string | undefined
  singleSide: boolean
  setSingleSide: (val: boolean) => void
  switchTokenA: () => void
  approvalAForSingleSide: ApprovalState
  approveACallbackForSingleSide: () => void
  showApprovalAForSingleSide: boolean
  presetPriceRange: PresetPriceRange
  setTriggerInfiniteRange: (val: boolean) => void
  handleStep: (step: number) => void
  onAddSingleSide: () => void
  currencyBalances: Record<string, any>
  errorSingleSide: string
  tokenId: string | undefined
}
const Step3: React.FC<Step3Props> = ({
  activeStep,
  tickLower,
  tickUpper,
  invalidPool,
  invalidRange,
  hasExistingPosition,
  formattedAmounts,
  maxAmounts,
  atMaxAmounts,
  currencies,
  usdcValues,
  onFieldAInput,
  onFieldBInput,
  depositADisabled,
  depositBDisabled,
  addIsUnsupported,
  account,
  toggleWalletModal,
  approvalA,
  approvalB,
  approveACallback,
  approveBCallback,
  showApprovalA,
  showApprovalB,
  isValid,
  parsedAmounts,
  expertMode,
  onAdd,
  setShowConfirm,
  attemptingTxn,
  errorMessage,
  singleSide,
  setSingleSide,
  switchTokenA,
  approvalAForSingleSide,
  approveACallbackForSingleSide,
  showApprovalAForSingleSide,
  presetPriceRange,
  setTriggerInfiniteRange,
  handleStep,
  onAddSingleSide,
  currencyBalances,
  errorSingleSide,
  tokenId,
}) => {
  const [modalIsOpen, setModalIsOpen] = React.useState(false)
  const getSingleSideError = useCallback(() => {
    if (!singleSide) return ''

    if (!parsedAmounts?.[Field.CURRENCY_A]) {
      return 'Enter an amount'
    }
    if (
      parsedAmounts?.[Field.CURRENCY_A] &&
      currencyBalances?.[Field.CURRENCY_A]?.lessThan(parsedAmounts?.[Field.CURRENCY_A] as TokenAmount)
    ) {
      return 'Insufficient ' + currencies[Field.CURRENCY_A]?.symbol + ' balance'
    }
    return ''
  }, [singleSide, parsedAmounts, currencyBalances, currencies])

  const getButtonLabel = () => {
    const approvalLoading =
      approvalA === ApprovalState.PENDING ||
      approvalB === ApprovalState.PENDING ||
      approvalA === ApprovalState.UNKNOWN ||
      approvalB === ApprovalState.UNKNOWN

    if (errorMessage) {
      return errorMessage
    } else if (
      approvalLoading &&
      parsedAmounts[Field.CURRENCY_A] !== undefined &&
      parsedAmounts[Field.CURRENCY_B] !== undefined
    ) {
      return <Dots>Checking allowance</Dots>
    } else if (attemptingTxn) {
      return <Dots>Loading</Dots>
    } else {
      return 'Add'
    }
  }

  return (
    <>
      <Modal isOpen={modalIsOpen} onDismiss={() => setModalIsOpen(false)}>
        <AutoColumn gap="lg" style={{ padding: '18px' }}>
          <TYPE.mediumHeader>
            {!tokenId ? 'Switch to Max Range' : 'Single Asset Zap is not available'}
          </TYPE.mediumHeader>
          <TYPE.main>
            {!tokenId
              ? 'Single Asset Zap liquidity provision is only available with max range price preset.'
              : 'Single Asset Zap is not available when increasing a position.'}
          </TYPE.main>
          <ButtonPrimary
            onClick={() => {
              setModalIsOpen(false)
              if (tokenId) {
                return
              }
              setSingleSide(true)

              if (setTriggerInfiniteRange && !tokenId) {
                setTriggerInfiniteRange(true)
                handleStep(1)
              }
            }}
          >
            {!tokenId ? 'Switch to Max Range' : 'Close'}
          </ButtonPrimary>
        </AutoColumn>
      </Modal>
      {activeStep === 3 && (
        <>
          <DynamicSection disabled={tickLower === undefined || tickUpper === undefined || invalidPool || invalidRange}>
            <AutoColumn gap="md" style={{ maxWidth: '500px' }}>
              <TYPE.label>{hasExistingPosition ? 'Add more liquidity' : 'Deposit Amounts'}</TYPE.label>

              <RowBetween>
                <ToggleWrapper>
                  <ToggleOption active={!singleSide} onClick={() => setSingleSide(false)}>
                    Dual Token Mode
                  </ToggleOption>
                  <ToggleOption
                    active={singleSide}
                    onClick={() => {
                      if (tokenId) {
                        setModalIsOpen(true)
                        return
                      }

                      if (presetPriceRange !== 'maxRange') {
                        setModalIsOpen(true)
                      } else {
                        setSingleSide(true)
                      }
                    }}
                  >
                    Single Asset Zap
                  </ToggleOption>
                </ToggleWrapper>
              </RowBetween>

              {singleSide ? (
                <TYPE.body fontWeight={400} fontSize={14} color={'text2'}>
                  Single Asset Zap allows you to add liquidity using only one token. The protocol will automatically
                  swap half of your input token to the other token in the pair, creating a balanced liquidity position.
                  This simplifies the process by eliminating the need to acquire both tokens separately.
                </TYPE.body>
              ) : (
                <></>
              )}

              <CurrencyInputPanel
                value={formattedAmounts[Field.CURRENCY_A]}
                onUserInput={onFieldAInput}
                onMax={() => {
                  onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                }}
                showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                currency={currencies[Field.CURRENCY_A]}
                id="add-liquidity-input-tokena"
                fiatValue={usdcValues[Field.CURRENCY_A]}
                showCommonBases
                locked={depositADisabled}
              />
              {singleSide && (
                <ArrowWrapper clickable style={{ marginTop: '-6px', marginBottom: '-6px' }}>
                  <svg
                    onClick={() => {
                      switchTokenA()
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4AFB39"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-switch-vertical"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M3 8l4 -4l4 4" />
                    <path d="M7 4l0 9" />
                    <path d="M13 16l4 4l4 -4" />
                    <path d="M17 10l0 10" />
                  </svg>
                </ArrowWrapper>
              )}

              <CurrencyInputPanel
                value={singleSide ? '0' : formattedAmounts[Field.CURRENCY_B]}
                onUserInput={onFieldBInput}
                onMax={() => {
                  onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                }}
                showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                fiatValue={usdcValues[Field.CURRENCY_B]}
                currency={currencies[Field.CURRENCY_B]}
                id="add-liquidity-input-tokenb"
                showCommonBases
                locked={depositBDisabled || singleSide}
                lockedMsg={singleSide ? 'This field is locked in Single Asset Zap' : null}
              />
            </AutoColumn>
          </DynamicSection>
          <div>
            {addIsUnsupported ? (
              <ButtonPrimary disabled={true} borderRadius="12px" padding={'12px'}>
                <TYPE.main mb="4px">Unsupported Asset</TYPE.main>
              </ButtonPrimary>
            ) : !account ? (
              <ButtonLight onClick={toggleWalletModal} borderRadius="12px" padding={'12px'}>
                Connect Wallet
              </ButtonLight>
            ) : (
              <AutoColumn gap={'md'}>
                {singleSide ? (
                  <>
                    {(approvalAForSingleSide === ApprovalState.NOT_APPROVED ||
                      approvalAForSingleSide === ApprovalState.PENDING) && (
                      <>
                        <RowBetween>
                          {showApprovalAForSingleSide && (
                            <>
                              <ButtonPrimary
                                borderRadius="12px"
                                padding={'12px'}
                                onClick={approveACallbackForSingleSide}
                                disabled={approvalAForSingleSide === ApprovalState.PENDING}
                                width={'100%'}
                              >
                                {approvalAForSingleSide === ApprovalState.PENDING ? (
                                  <Dots>Approving {currencies[Field.CURRENCY_A]?.symbol}</Dots>
                                ) : (
                                  'Approve ' + currencies[Field.CURRENCY_A]?.symbol
                                )}
                              </ButtonPrimary>
                            </>
                          )}
                        </RowBetween>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {(approvalA === ApprovalState.NOT_APPROVED ||
                      approvalA === ApprovalState.PENDING ||
                      approvalB === ApprovalState.NOT_APPROVED ||
                      approvalB === ApprovalState.PENDING) &&
                      isValid && (
                        <RowBetween>
                          {showApprovalA && (
                            <ButtonPrimary
                              borderRadius="12px"
                              padding={'12px'}
                              onClick={approveACallback}
                              disabled={approvalA === ApprovalState.PENDING}
                              width={showApprovalB ? '48%' : '100%'}
                            >
                              {approvalA === ApprovalState.PENDING ? (
                                <Dots>Approving {currencies[Field.CURRENCY_A]?.symbol}</Dots>
                              ) : (
                                'Approve ' + currencies[Field.CURRENCY_A]?.symbol
                              )}
                            </ButtonPrimary>
                          )}
                          {showApprovalB && (
                            <ButtonPrimary
                              borderRadius="12px"
                              padding={'12px'}
                              onClick={approveBCallback}
                              disabled={approvalB === ApprovalState.PENDING}
                              width={showApprovalA ? '48%' : '100%'}
                            >
                              {approvalB === ApprovalState.PENDING ? (
                                <Dots>Approving {currencies[Field.CURRENCY_B]?.symbol}</Dots>
                              ) : (
                                'Approve ' + currencies[Field.CURRENCY_B]?.symbol
                              )}
                            </ButtonPrimary>
                          )}
                        </RowBetween>
                      )}
                  </>
                )}
                <ButtonError
                  onClick={() => {
                    expertMode ? (singleSide ? onAddSingleSide() : onAdd()) : setShowConfirm(true)
                  }}
                  disabled={
                    singleSide
                      ? !parsedAmounts[Field.CURRENCY_A]?.greaterThan(0) ||
                        approvalAForSingleSide !== ApprovalState.APPROVED
                      : !isValid ||
                        (approvalA !== ApprovalState.APPROVED && !depositADisabled) ||
                        (approvalB !== ApprovalState.APPROVED && !depositBDisabled) ||
                        attemptingTxn === true
                  }
                  error={
                    singleSide
                      ? !parsedAmounts[Field.CURRENCY_A]
                      : !isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]
                  }
                >
                  <Text fontWeight={500}>
                    {singleSide ? (getSingleSideError() ? getSingleSideError() : 'Add') : getButtonLabel()}
                  </Text>
                </ButtonError>
                {errorSingleSide && singleSide && <TYPE.body color={'red'}>{errorSingleSide}</TYPE.body>}
              </AutoColumn>
            )}
          </div>
        </>
      )}
    </>
  )
}
export default React.memo(Step3)
